import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth, canReview } from "@/lib/auth";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import {
  ArrowLeft, Play, CheckCircle2, XCircle, Camera, X, Loader2, ShieldCheck, AlertTriangle,
  MapPin, Clock, User, ClipboardList,
} from "lucide-react";

function AuthedImage({ path, className, alt = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    api.get(`/uploads/file`, { params: { path }, responseType: "blob" }).then((r) => {
      if (cancelled) return;
      url = URL.createObjectURL(r.data);
      setSrc(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className={`${className} bg-muted animate-pulse`} />;
  return <img src={src} className={className} alt={alt} />;
}

function ItemInput({ item, value, onChange, disabled }) {
  const type = item.type;
  const v = value || {};
  if (type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <Checkbox
          checked={!!v.value}
          onCheckedChange={(c) => onChange({ ...v, value: !!c, passed: !!c })}
          disabled={disabled}
          data-testid={`item-check-${item.id}`}
          className="h-5 w-5 rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
        <span className="text-sm text-muted-foreground">Mark complete</span>
      </label>
    );
  }
  if (type === "yes_no") {
    return (
      <div className="flex items-center gap-2">
        <Button size="sm" variant={v.value === true ? "default" : "outline"} onClick={() => onChange({ ...v, value: true, passed: true })} disabled={disabled} data-testid={`item-yes-${item.id}`}>Yes</Button>
        <Button size="sm" variant={v.value === false ? "destructive" : "outline"} onClick={() => onChange({ ...v, value: false, passed: false })} disabled={disabled} data-testid={`item-no-${item.id}`}>No</Button>
      </div>
    );
  }
  if (type === "number" || type === "temperature") {
    return (
      <Input
        type="number"
        step="any"
        value={v.value ?? ""}
        onChange={(e) => onChange({ ...v, value: e.target.value === "" ? null : Number(e.target.value), passed: true })}
        disabled={disabled}
        className="w-40"
        placeholder={type === "temperature" ? "°C" : ""}
        data-testid={`item-number-${item.id}`}
      />
    );
  }
  if (type === "text") {
    return <Textarea rows={2} value={v.value || ""} onChange={(e) => onChange({ ...v, value: e.target.value, passed: true })} disabled={disabled} data-testid={`item-text-${item.id}`} />;
  }
  if (type === "signature") {
    return (
      <SignaturePad
        testId={`item-sig-${item.id}`}
        initial={v.signature_url_preview}
        onChange={(dataUrl) => onChange({ ...v, signature_data_url: dataUrl, passed: !!dataUrl })}
      />
    );
  }
  if (type === "photo") {
    return null; // handled below in evidence uploader
  }
  return null;
}

export default function TaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data: task, isLoading } = useQuery({
    queryKey: ["task", id],
    queryFn: async () => (await api.get(`/tasks/${id}`)).data,
  });
  const { data: template } = useQuery({
    queryKey: ["template-detail", task?.template_id],
    queryFn: async () => (await api.get(`/checklists/${task.template_id}`)).data,
    enabled: !!task?.template_id,
  });
  const { data: users } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const userMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const [items, setItems] = useState({});
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [uploadingItem, setUploadingItem] = useState(null);

  useEffect(() => {
    if (task?.completion_items?.length) {
      const map = {};
      task.completion_items.forEach((c) => {
        map[c.item_id] = {
          value: c.value, notes: c.notes, photo_urls: c.photo_urls || [], signature_url: c.signature_url, passed: c.passed,
        };
      });
      setItems(map);
      setNotes(task.completion_notes || "");
    }
  }, [task]);

  if (isLoading || !task) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const isAssignee = task.assigned_to === user.id;
  const editable = ["assigned", "in_progress", "rejected"].includes(task.status) && (isAssignee || canReview(user));
  const canVerify = canReview(user) && task.status === "completed";

  const handleStart = async () => {
    try { await api.post(`/tasks/${id}/start`); qc.invalidateQueries({ queryKey: ["task", id] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadPhoto = async (itemId, file) => {
    setUploadingItem(itemId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((s) => ({
        ...s,
        [itemId]: {
          ...(s[itemId] || {}),
          photo_urls: [...((s[itemId] || {}).photo_urls || []), res.data.storage_path],
        },
      }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingItem(null); }
  };

  const removePhoto = (itemId, url) => {
    setItems((s) => ({
      ...s,
      [itemId]: {
        ...(s[itemId] || {}),
        photo_urls: ((s[itemId] || {}).photo_urls || []).filter((u) => u !== url),
      },
    }));
  };

  const uploadSignatureIfNeeded = async () => {
    let sigUrl = null;
    if (signature && signature.startsWith("data:")) {
      const res = await api.post("/uploads/data-url", { data_url: signature, folder: "signatures" });
      sigUrl = res.data.storage_path;
    }
    // per-item signatures
    const updatedItems = {};
    for (const [iid, v] of Object.entries(items)) {
      const copy = { ...v };
      if (v.signature_data_url) {
        const r = await api.post("/uploads/data-url", { data_url: v.signature_data_url, folder: "signatures" });
        copy.signature_url = r.data.storage_path;
        delete copy.signature_data_url;
      }
      updatedItems[iid] = copy;
    }
    return { sigUrl, updatedItems };
  };

  const submit = async () => {
    if (!template) return;
    // validate required
    for (const it of template.items) {
      if (!it.required) continue;
      const v = items[it.id];
      if (!v || v.value == null || v.value === "" || (it.type === "signature" && !(v.signature_data_url || v.signature_url))) {
        return toast.error(`Missing: ${it.label}`);
      }
      if (it.evidence_required && !(v.photo_urls?.length)) {
        return toast.error(`Photo required for: ${it.label}`);
      }
    }
    setSubmitting(true);
    try {
      const { sigUrl, updatedItems } = await uploadSignatureIfNeeded();
      const payload = {
        signature_url: sigUrl,
        completion_notes: notes,
        items: template.items.map((it) => {
          const v = updatedItems[it.id] || {};
          return {
            item_id: it.id,
            value: v.value ?? null,
            notes: v.notes ?? null,
            photo_urls: v.photo_urls || [],
            signature_url: v.signature_url || null,
            passed: v.passed !== false,
          };
        }),
      };
      await api.post(`/tasks/${id}/complete`, payload);
      toast.success("Task submitted for review");
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const verify = async (approve) => {
    try {
      await api.post(`/tasks/${id}/verify`, { approve, reason: approve ? null : rejectReason });
      toast.success(approve ? "Task verified" : "Task rejected");
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      setRejectOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusBadge = {
    pending: "outline", assigned: "default", in_progress: "secondary",
    completed: "secondary", verified: "default", rejected: "destructive",
    overdue: "destructive", cancelled: "outline",
  }[task.status];

  const totalItems = template?.items?.length || 0;
  const completedItems = template?.items?.filter((it) => {
    const v = items[it.id];
    if (!v) return false;
    if (it.type === "checkbox" || it.type === "yes_no") return v.value != null;
    if (it.type === "signature") return !!(v.signature_data_url || v.signature_url);
    return v.value !== null && v.value !== undefined && v.value !== "";
  }).length || 0;
  const progressPct = totalItems ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto md:mx-0">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="border border-border bg-card rounded-2xl p-6 mb-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground mb-2 font-semibold">Task</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{task.title}</h1>
          </div>
          <span
            className="shrink-0 text-xs font-semibold capitalize px-3 py-1.5 rounded-full whitespace-nowrap"
            style={
              task.status === "verified" || task.status === "in_progress"
                ? { background: "hsl(158 74% 92%)", color: "hsl(158 74% 30%)" }
                : task.status === "overdue" || task.status === "rejected"
                ? { background: "hsl(0 78% 94%)", color: "hsl(0 78% 45%)" }
                : task.status === "assigned"
                ? { background: "hsl(38 92% 90%)", color: "hsl(38 92% 35%)" }
                : { background: "hsl(220 15% 92%)", color: "hsl(220 30% 30%)" }
            }
            data-testid="task-status"
          >
            {task.status.replace("_", " ")}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3">
            <span className="flex items-center gap-2 text-muted-foreground"><Clock className="w-3.5 h-3.5" />Due Time</span>
            <span className="font-semibold text-foreground">{new Date(task.due_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3">
            <span className="flex items-center gap-2 text-muted-foreground"><User className="w-3.5 h-3.5" />Assignee</span>
            <span className="font-semibold text-foreground truncate max-w-[10rem]">{userMap[task.assigned_to]?.name || "—"}</span>
          </div>
          <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3">
            <span className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-3.5 h-3.5" />Supervisor</span>
            <span className="font-semibold text-foreground truncate max-w-[10rem]">{userMap[task.supervisor_id]?.name || "—"}</span>
          </div>
          <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3">
            <span className="flex items-center gap-2 text-muted-foreground">Priority</span>
            <span className="font-semibold capitalize">{task.priority}</span>
          </div>
        </div>

        {task.rejection_reason && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-xl text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-semibold">Rejected</div>
              <div>{task.rejection_reason}</div>
            </div>
          </div>
        )}

        {(task.shift_area_name || task.completed_by_qr_token) && (
          <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-xl text-xs">
            <div className="label-caps text-primary font-semibold mb-1">Attendance link</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-foreground">
              {task.shift_area_name && (
                <span>Completed while clocked in at <span className="font-semibold">{task.shift_area_name}</span></span>
              )}
              {task.completed_by_qr_token && (
                <span className="font-mono text-muted-foreground">QR #{task.completed_by_qr_token.slice(0, 8)}</span>
              )}
            </div>
          </div>
        )}

        {task.status === "assigned" && isAssignee && (
          <div className="mt-6">
            <Button onClick={handleStart} className="w-full h-12 rounded-2xl text-base font-semibold" data-testid="start-task">
              <Play className="w-5 h-5 mr-2" />Start Task
            </Button>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {template && editable && (
        <div className="border border-border bg-card rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">{completedItems} of {totalItems}</span>
            <span className="text-sm font-bold text-primary">{progressPct}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="border border-border bg-card rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold">Checklist</div>
            <div className="font-display text-lg font-semibold">{template?.name || "Loading…"}</div>
          </div>
          <ClipboardList className="w-4 h-4 text-muted-foreground" />
        </div>

        <div className="divide-y divide-border">
          {template?.items.map((it, idx) => {
            const v = items[it.id] || {};
            return (
              <div key={it.id} className="p-5" data-testid={`checklist-item-${idx}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium mb-2">
                      {idx + 1}. {it.label}{it.required && <span className="text-destructive ml-0.5">*</span>}
                    </div>
                    <ItemInput item={it} value={v} onChange={(nv) => setItems((s) => ({ ...s, [it.id]: nv }))} disabled={!editable} />
                    {it.notes_allowed && editable && (
                      <Textarea
                        className="mt-3 max-w-md"
                        rows={1}
                        placeholder="Notes (optional)"
                        value={v.notes || ""}
                        onChange={(e) => setItems((s) => ({ ...s, [it.id]: { ...(s[it.id] || {}), notes: e.target.value } }))}
                      />
                    )}
                    {!editable && v.notes && <div className="mt-2 text-xs text-muted-foreground italic">"{v.notes}"</div>}
                  </div>

                  {/* Evidence photos */}
                  <div className="w-full md:w-64 shrink-0">
                    {(v.photo_urls?.length > 0 || it.evidence_required || it.type === "photo") && (
                      <>
                        <div className="label-caps text-muted-foreground mb-2">Evidence {it.evidence_required && <span className="text-destructive">*</span>}</div>
                        <div className="grid grid-cols-3 gap-1">
                          {(v.photo_urls || []).map((p) => (
                            <div key={p} className="relative aspect-square border border-border overflow-hidden">
                              <AuthedImage path={p} className="w-full h-full object-cover" />
                              {editable && (
                                <button
                                  className="absolute top-1 right-1 bg-background/90 rounded-full p-0.5 hover:bg-destructive hover:text-destructive-foreground transition-colors"
                                  onClick={() => removePhoto(it.id, p)}
                                  aria-label="Remove photo"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          {editable && (
                            <label className="aspect-square border border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                              {uploadingItem === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 text-muted-foreground" />}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadPhoto(it.id, f);
                                  e.target.value = "";
                                }}
                                data-testid={`upload-photo-${idx}`}
                              />
                            </label>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {editable && (
          <div className="px-6 py-5 border-t border-border space-y-4">
            <div>
              <Label>Overall notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="completion-notes" />
            </div>
            <div>
              <Label>Final signature</Label>
              <SignaturePad onChange={setSignature} initial={task.signature_url} testId="final-signature" />
            </div>
            <div className="flex justify-end">
              <Button onClick={submit} disabled={submitting} className="h-12 rounded-2xl px-6 text-base font-semibold" data-testid="submit-task">
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                Complete Task
              </Button>
            </div>
          </div>
        )}

        {canVerify && (
          <div className="px-6 py-5 border-t border-border flex items-center justify-between gap-3 flex-wrap bg-primary/5">
            <div>
              <div className="font-medium text-sm">Supervisor review</div>
              <div className="text-xs text-muted-foreground">Approve or reject this completion.</div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="reject-task">
                <XCircle className="w-4 h-4 mr-1 text-destructive" />Reject
              </Button>
              <Button onClick={() => verify(true)} data-testid="verify-task">
                <CheckCircle2 className="w-4 h-4 mr-1" />Verify
              </Button>
            </div>
          </div>
        )}

        {task.signature_url && !editable && (
          <div className="px-6 py-5 border-t border-border">
            <div className="label-caps text-muted-foreground mb-2">Signature</div>
            <AuthedImage path={task.signature_url} className="max-w-xs h-24 object-contain bg-white border border-border" />
          </div>
        )}
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject task</DialogTitle></DialogHeader>
          <Label>Reason</Label>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="Explain what needs to be redone" data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => verify(false)} data-testid="confirm-reject">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
